import { useMutation } from "@apollo/client";
import { getFormProps, useForm } from "@conform-to/react";
import { parseWithZod } from "@conform-to/zod";
import { ColorPicker } from "framework7/types";
import { Block, Button, List, ListInput } from "framework7-react";
import { useState } from "react";

import { UpsertUserCategoryDocument, UserCategoryFragment, UserCategoryInput } from "@/generated/graphql/graphql";
import { UserCategoryInputSchema } from "@/generated/graphql/graphql.zod";
import { useColorPalette } from "@/hooks/useColorPalette";

type CategoryFormProps = {
  userCategory: UserCategoryFragment;
  onCompleted: VoidFunction;
};

export function CategoryForm({ userCategory, onCompleted }: CategoryFormProps) {
  const [upsertUserCategory, { loading }] = useMutation(UpsertUserCategoryDocument);

  const [form, fields] = useForm<UserCategoryInput>({
    defaultValue: {
      color: userCategory.color,
      name: userCategory.name,
    },
    onSubmit(event, { formData }) {
      event.preventDefault();

      const submission = parseWithZod(formData, { schema: UserCategoryInputSchema });

      if (submission.status !== "success") {
        return submission.reply();
      }

      return upsertUserCategory({
        onCompleted: () => onCompleted(),
        variables: {
          categoryKey: userCategory.categoryKey,
          userCategoryInput: {
            color: submission.value.color,
            name: submission.value.name,
          },
        },
      });
    },
    onValidate({ formData }) {
      return parseWithZod(formData, { schema: UserCategoryInputSchema });
    },
  });

  const colorPalette = useColorPalette();
  const [color, setColor] = useState<string>(userCategory.color);

  return (
    <form {...getFormProps(form)}>
      <input
        name={fields.color.name}
        type="hidden"
        value={color}
      />

      <List inset>
        <ListInput
          required
          validate
          defaultValue={fields.name.value}
          errorMessage={fields.name.errors?.join("\n")}
          label="カテゴリー名"
          name={fields.name.name}
          type="text"
        />

        <ListInput
          readonly
          colorPickerParams={{
            groupedModules: true,
            hexLabel: true,
            hexLabelText: "カテゴリの色 (HEX)",
            hexValueEditable: true,
            modules: ["hex", "palette"],
            navbarBackLinkText: "戻る",
            navbarCloseText: "完了",
            navbarTitleText: "色を選択",
            openIn: "auto",
            openInPhone: "sheet",
            palette: colorPalette,
            sliderLabel: true,
            sliderValue: true,
            targetElSetBackgroundColor: false,
            toolbarCloseText: "完了",
            value: { hex: color },
          }}
          label="カテゴリの色"
          placeholder="Color"
          type="colorpicker"
          onColorPickerChange={(value: ColorPicker.ColorPickerValue) => {
            if (value?.hex) {
              setColor(value.hex);
            }
          }}
        >
          <i
            className="icon"
            slot="media"
            style={{
              backgroundColor: color,
              borderRadius: 6,
              boxSizing: "border-box",
              height: 28,
              width: 28,
            }}
          />
        </ListInput>
      </List>

      <Block>
        <div
          className="grid grid-cols-2 grid-gap"
          style={{ padding: "var(--f7-safe-area-bottom)" }}
        >
          <Button
            outline
            disabled={loading}
            type="button"
          >
            キャンセル
          </Button>
          <Button
            fill
            loading={loading}
            type="submit"
          >
            保存する
          </Button>
        </div>
      </Block>
    </form>
  );
}
